import client from '@/utils/httpClient';
import { PagedApiResponse, Pagination } from '@oncolens/oncolens-ui/lib/dto/api/ApiResponse';
import { AxiosError } from 'axios';
import { UseMutationOptions, useMutation, useQuery, useQueryClient } from 'react-query';

export const guidelineKeys = {
  base: ['guidelines'] as const,
  byId: (id: string) => [...guidelineKeys.base, 'byId', id],
  list: () => [...guidelineKeys.base, 'list'],
};

export const GuideLineType = ['ASCO', 'NCCN', 'ESMO', 'Other'] as const;
export interface GuidelineDto {
  id: string;
  name: string;
  type: typeof GuideLineType;
  lastUpdated: Date;
  fileLastUpdated: Date;
  presignedUrl: string;
  filename?: string;
  url?: string;
  organizationIds: string[];
  assignAllOrganizations: boolean;
  diseaseConditions: GuidelineConditionDto[];
}

export interface GuidelineForCreationDto {
  name: string;
  type: string;
  url?: string;
  file?: File;
  diseaseConditions: GuidelineConditionForCreationDto[];
  organizations: {
    assignAllOrganizations: boolean;
    organizationIds: string[];
  };
}

export interface GuidelineConditionForCreationDto {
  cancerTypeId?: string;
  cancerPathologyId?: string;
  cancerLocationId?: string;
  sortOrder: number;
}

export interface GuidelineForUpdateDto {
  name: string;
  type: string;
  url?: string;
  diseaseConditions: GuidelineConditionForUpdateDto[];
  organizations: {
    assignAllOrganizations: boolean;
    organizationIds: string[];
  };
}

export interface GuidelineConditionForUpdateDto {
  cancerTypeId?: string;
  cancerPathologyId?: string;
  cancerLocationId?: string;
  sortOrder: number;
}

export interface GuidelineConditionDto {
  id: string;
  cancerTypeId?: string;
  cancerPathologyId?: string;
  cancerLocationId?: string;
  sortOrder: number;
}

function getGuideline(id: string) {
  return client.get<GuidelineDto>(`/guidelines/${id}`).then((response) => response.data);
}

function useGuideline(id: string | undefined) {
  const identifier = id ?? '';
  return useQuery(guidelineKeys.byId(identifier), () => getGuideline(identifier), {
    enabled: id !== undefined,
  });
}

function getGuidelines(query: string) {
  return client.get<GuidelineDto[]>(`/guidelines?${query}`).then((response) => {
    const data = response.data as GuidelineDto[];

    return {
      data,
      pagination: JSON.parse(response.headers['x-pagination']) as Pagination,
    } as PagedApiResponse<GuidelineDto>;
  });
}

function useGuidelines(query: string) {
  return useQuery(guidelineKeys.list(), () => getGuidelines(query));
}

function deleteGuideline(id: string) {
  return client.delete(`/guidelines/${id}`);
}

function useDeleteGuideline() {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const client = useQueryClient();

  return useMutation((id: string) => deleteGuideline(id), {
    onSuccess: () => {
      client.invalidateQueries(guidelineKeys.base);
    },
  });
}

export const updateGuideline = async ({
  id,
  data,
}: {
  id: string | undefined;
  data: GuidelineForUpdateDto;
}) => client.put(`/guidelines/${id}`, data).then((response) => response.data);
export interface UpdateProps {
  id: string;
  data: GuidelineForUpdateDto;
}
export function useUpdateGuideline(
  id: string | undefined,
  options?: UseMutationOptions<void, AxiosError, GuidelineForUpdateDto>,
) {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const client = useQueryClient();
  return useMutation((data) => updateGuideline({ id, data }), {
    onSuccess: () => {
      if (id !== undefined) client.invalidateQueries(guidelineKeys.byId(id));
    },
    ...options,
  });
}

function updateGuidelineFile({
  guidelineId,
  file,
}: {
  guidelineId: string | undefined;
  file: File;
}) {
  const formData = new FormData();

  if (file === undefined) throw new Error('File is undefined');

  formData.append('file', file, file.name);

  return client
    .put(`/guidelines/${guidelineId}/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response) => response.data);
}

function useUpdateGuidelineFile(
  guidelineId: string | undefined,
  options?: UseMutationOptions<void, AxiosError, File>,
) {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const client = useQueryClient();
  return useMutation((file) => updateGuidelineFile({ guidelineId, file }), {
    onSuccess: () => {
      if (guidelineId !== undefined) client.invalidateQueries(guidelineKeys.byId(guidelineId));
    },
    ...options,
  });
}

function createGuideline(guideline: GuidelineForCreationDto) {
  const formData = new FormData();

  formData.append('name', guideline.name);
  formData.append('type', guideline.type);

  if (!guideline.file && !guideline.url) throw new Error('File or URL must be provided');

  if (guideline.file) {
    formData.append('file', guideline.file!, guideline.file!.name);
  }

  if (guideline.url) {
    formData.append('url', guideline.url);
  }

  guideline.organizations.organizationIds.forEach((id, index) => {
    formData.append(`organizations.organizationIds[${index}]`, id);
  });

  formData.append(
    'organizations.assignAllOrganizations',
    guideline.organizations.assignAllOrganizations.toString(),
  );

  guideline.diseaseConditions.forEach((condition, index) => {
    formData.append(`diseaseConditions[${index}].cancerTypeId`, condition.cancerTypeId || '');
    formData.append(
      `diseaseConditions[${index}].cancerPathologyId`,
      condition.cancerPathologyId || '',
    );
    formData.append(
      `diseaseConditions[${index}].cancerLocationId`,
      condition.cancerLocationId || '',
    );
  });

  return client
    .post('/guidelines', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((response) => response.data);
}

function useCreateGuideline() {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const client = useQueryClient();

  return useMutation((guideline: GuidelineForCreationDto) => createGuideline(guideline), {
    onSuccess: () => {
      client.invalidateQueries(guidelineKeys.list());
    },
  });
}

export {
  getGuidelines,
  useCreateGuideline,
  useDeleteGuideline,
  useGuideline,
  useGuidelines,
  useUpdateGuidelineFile,
};
